import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, Upload, FileText, Trash2, Download, Link2, Copy, CheckCircle2, AlertTriangle, X, FileDown, Loader2, Eye, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { key: 'lca', label: 'Labor Condition Application (LCA)', description: 'Certified LCA from DOL' },
  { key: 'wage_documentation', label: 'Wage Documentation', description: 'Actual wage and prevailing wage records' },
  { key: 'wage_determination', label: 'Wage Determination Explanation', description: 'Methodology for wage determination' },
  { key: 'employee_notification', label: 'Employee Notification Proof', description: 'Notice to employee of LCA filing' },
  { key: 'benefits_summary', label: 'Benefits Summary', description: 'Summary of benefits offered to H-1B, H-1B1, and E-3 workers' },
];

export default function PAFDetailPage() {
  const { pafId } = useParams();
  const navigate = useNavigate();
  const { apiCall, user } = useAuth();
  const [paf, setPaf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [replacing, setReplacing] = useState(null);
  const fileInputRefs = useRef({});
  const replaceInputRefs = useRef({});
  const canEdit = user?.role !== 'viewer';

  const fetchPAF = useCallback(async () => {
    try {
      const res = await apiCall('get', `/pafs/${pafId}`);
      setPaf(res.data);
    } catch (err) {
      toast.error('Failed to load PAF');
      navigate('/pafs');
    } finally {
      setLoading(false);
    }
  }, [apiCall, pafId, navigate]);

  useEffect(() => { fetchPAF(); }, [fetchPAF]);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiCall('post', `/documents/upload?paf_id=${pafId}&doc_type=${docType}`, formData);
      toast.success(`${file.name} uploaded`);
      if (docType === 'lca' && file.type === 'application/pdf') {
        toast.info('Extracting LCA data to auto-fill employee fields...', { duration: 4000 });
      }
      fetchPAF();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await apiCall('get', `/documents/${doc.id}/download`, null, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = doc.original_filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  const handleDeleteDoc = async (doc) => {
    setDeleteDoc(doc);
  };

  const confirmDeleteDoc = async () => {
    if (!deleteDoc) return;
    try {
      await apiCall('delete', `/documents/${deleteDoc.id}`);
      toast.success('Document deleted');
      fetchPAF();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleteDoc(null);
    }
  };

  const handleViewDoc = async (doc) => {
    setViewDoc(doc);
    setViewLoading(true);
    try {
      const res = await apiCall('get', `/documents/${doc.id}/download`, null, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || doc.content_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      setViewUrl(url);
    } catch (err) {
      toast.error('Failed to load document preview');
      setViewDoc(null);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewDoc = () => {
    if (viewUrl) window.URL.revokeObjectURL(viewUrl);
    setViewDoc(null);
    setViewUrl(null);
  };

  const handleReplace = async (doc, file) => {
    if (!file) return;
    setReplacing(doc.id);
    try {
      // Delete old document
      await apiCall('delete', `/documents/${doc.id}`);
      // Upload new one
      const formData = new FormData();
      formData.append('file', file);
      await apiCall('post', `/documents/upload?paf_id=${pafId}&doc_type=${doc.doc_type}`, formData);
      toast.success(`Replaced with ${file.name}`);
      fetchPAF();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replace failed');
    } finally {
      setReplacing(null);
    }
  };

  const togglePublicAccess = async () => {
    try {
      await apiCall('put', `/pafs/${pafId}`, { public_access_enabled: !paf.public_access_enabled });
      fetchPAF();
      toast.success(paf.public_access_enabled ? 'Public access disabled' : 'Public access enabled');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const copyPublicLink = () => {
    const link = `${window.location.origin}/public/paf/${paf.public_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Public link copied to clipboard');
  };

  const handleDrop = (e, docType) => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(docType, file);
  };

  const handleGeneratePacket = async () => {
    setGenerating(true);
    try {
      const res = await apiCall('get', `/pafs/${pafId}/generate-packet`, null, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const fname = res.headers?.['content-disposition']?.match(/filename="(.+?)"/)?.[1]
        || `PAF_Packet_${emp.first_name}_${emp.last_name}.pdf`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 200);
      toast.success('PAF Packet generated and downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate PAF packet');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!paf) return null;

  const emp = paf.employee || {};
  const docs = paf.documents || [];
  const statusColor = {
    compliant: { bg: '#ECFDF5', text: '#059669', label: 'Compliant' },
    warning: { bg: '#FFFBEB', text: '#D97706', label: 'Warning' },
    non_compliant: { bg: '#FEF2F2', text: '#DC2626', label: 'Non-Compliant' },
  }[paf.compliance_status] || { bg: '#FEF2F2', text: '#DC2626', label: 'Non-Compliant' };

  return (
    <div data-testid="paf-detail-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        {/* Header */}
        <button onClick={() => navigate('/pafs')} className="flex items-center gap-2 text-[#4F46E5] hover:underline text-sm font-medium mb-6" data-testid="back-to-pafs">
          <ArrowLeft className="w-4 h-4" /> Back to PAFs
        </button>

        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">
              {emp.first_name} {emp.last_name}'s PAF
            </h1>
            <p className="text-[#64748B]">{emp.job_title} — {emp.department}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="generate-packet-btn"
              onClick={handleGeneratePacket}
              disabled={generating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-9 px-4 text-sm font-medium shadow-sm"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><FileDown className="w-4 h-4 mr-2" /> Generate PAF Packet</>
              )}
            </Button>
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: statusColor.bg, color: statusColor.text }}>
              {statusColor.label}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl border border-indigo-100/50 p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-[#0F172A]">Compliance Progress</h2>
            <span className="text-sm font-medium text-[#0F172A]">{paf.completion_percentage}%</span>
          </div>
          <Progress value={paf.completion_percentage} className="h-3 bg-[#F8FAFF]" />
          <p className="text-sm text-[#64748B] mt-2">
            {DOC_TYPES.filter(d => paf.checklist?.[d.key]).length} of {DOC_TYPES.length} required documents uploaded
          </p>
        </div>

        {/* Public Access */}
        <div className="bg-white rounded-2xl border border-indigo-100/50 p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.5} />
              <div>
                <h3 className="text-sm font-semibold text-[#0F172A]">Public Access Link</h3>
                <p className="text-xs text-[#64748B]">Share a read-only view of this PAF</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {paf.public_access_enabled && (
                <Button variant="outline" size="sm" onClick={copyPublicLink} className="rounded-full text-xs" data-testid="copy-public-link">
                  <Copy className="w-3 h-3 mr-1" /> Copy Link
                </Button>
              )}
              {canEdit && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-[#64748B]">{paf.public_access_enabled ? 'Enabled' : 'Disabled'}</Label>
                  <Switch checked={paf.public_access_enabled} onCheckedChange={togglePublicAccess} data-testid="toggle-public-access" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Document Checklist */}
        <h2 className="text-lg font-bold text-[#0F172A] tracking-tight mb-4">Required Documents</h2>
        <div className="space-y-4">
          {DOC_TYPES.map((docType) => {
            const isComplete = paf.checklist?.[docType.key];
            const typeDocs = docs.filter(d => d.doc_type === docType.key);
            const isUploading = uploading === docType.key;
            const isDraggedOver = dragOver === docType.key;

            return (
              <div
                key={docType.key}
                className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${
                  isComplete ? 'border-[#10B981]/30' : 'border-indigo-100/50'
                }`}
                data-testid={`doc-section-${docType.key}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isComplete ? 'bg-[#ECFDF5]' : 'bg-[#F8FAFF]'
                      }`}>
                        {isComplete ? (
                          <CheckCircle2 className="w-4 h-4 text-[#10B981]" strokeWidth={1.5} />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-[#64748B]" strokeWidth={1.5} />
                        )}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-[#0F172A]">{docType.label}</h3>
                        <p className="text-xs text-[#64748B]">{docType.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Uploaded files */}
                  {typeDocs.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {typeDocs.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-[#F8FAFF] rounded-xl" data-testid={`uploaded-doc-${doc.id}`}>
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-[#64748B]" strokeWidth={1.5} />
                            <div>
                              <p className="text-sm text-[#0F172A] font-medium">{doc.original_filename}</p>
                              <p className="text-xs text-[#64748B]">
                                {(doc.size_bytes / 1024).toFixed(1)} KB &middot; {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleViewDoc(doc)} className="p-1.5 rounded-lg hover:bg-white text-[#64748B] hover:text-[#4F46E5] transition-colors" title="View" data-testid={`view-doc-${doc.id}`}>
                              <Eye className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button onClick={() => handleDownload(doc)} className="p-1.5 rounded-lg hover:bg-white text-[#64748B] hover:text-[#4F46E5] transition-colors" title="Download" data-testid={`download-doc-${doc.id}`}>
                              <Download className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            {canEdit && (
                              <>
                                <button
                                  onClick={() => replaceInputRefs.current[doc.id]?.click()}
                                  className="p-1.5 rounded-lg hover:bg-white text-[#64748B] hover:text-amber-500 transition-colors"
                                  title="Replace"
                                  data-testid={`replace-doc-${doc.id}`}
                                  disabled={replacing === doc.id}
                                >
                                  {replacing === doc.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                                  )}
                                </button>
                                <input
                                  ref={el => replaceInputRefs.current[doc.id] = el}
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => { handleReplace(doc, e.target.files[0]); e.target.value = ''; }}
                                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                                />
                                <button onClick={() => handleDeleteDoc(doc)} className="p-1.5 rounded-lg hover:bg-white text-[#64748B] hover:text-[#EF4444] transition-colors" title="Delete" data-testid={`delete-doc-${doc.id}`}>
                                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload area */}
                  {canEdit && (
                    <div
                      className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
                        isDraggedOver ? 'border-[#4F46E5] bg-[#4F46E5]/5' : 'border-gray-300 bg-white hover:bg-gray-50'
                      }`}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(docType.key); }}
                      onDragLeave={() => setDragOver(null)}
                      onDrop={(e) => handleDrop(e, docType.key)}
                      onClick={() => fileInputRefs.current[docType.key]?.click()}
                      data-testid={`upload-zone-${docType.key}`}
                    >
                      <input
                        ref={el => fileInputRefs.current[docType.key] = el}
                        type="file"
                        className="hidden"
                        onChange={(e) => handleUpload(docType.key, e.target.files[0])}
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      />
                      {isUploading ? (
                        <div className="w-6 h-6 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin mx-auto" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-[#64748B] mx-auto mb-2" strokeWidth={1.5} />
                          <p className="text-sm text-[#64748B]">
                            Drag & drop or <span className="text-[#4F46E5] font-medium">browse</span>
                          </p>
                          <p className="text-xs text-[#64748B] mt-1">PDF, DOC, DOCX, PNG, JPG</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* View Document Modal */}
      <Dialog open={!!viewDoc} onOpenChange={(open) => { if (!open) closeViewDoc(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden" data-testid="view-doc-modal">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-base font-semibold text-[#0F172A] truncate">
              {viewDoc?.original_filename}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              {viewDoc?.doc_type && DOC_TYPES.find(d => d.key === viewDoc.doc_type)?.label}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto" style={{ height: '70vh' }}>
            {viewLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 text-[#4F46E5] animate-spin" />
              </div>
            ) : viewUrl ? (
              viewDoc?.content_type?.startsWith('image/') ? (
                <div className="flex items-center justify-center p-6 h-full bg-[#F8FAFF]">
                  <img src={viewUrl} alt={viewDoc?.original_filename} className="max-w-full max-h-full object-contain rounded-lg" />
                </div>
              ) : viewDoc?.content_type === 'application/pdf' ? (
                <iframe src={viewUrl} title={viewDoc?.original_filename} className="w-full h-full border-0" />
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-[#64748B]">
                  <FileText className="w-12 h-12" strokeWidth={1} />
                  <p className="text-sm">Preview not available for this file type</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => { handleDownload(viewDoc); closeViewDoc(); }}
                    data-testid="view-modal-download-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download to view
                  </Button>
                </div>
              )
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation Modal */}
      <Dialog open={!!deleteDoc} onOpenChange={(open) => { if (!open) setDeleteDoc(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-doc-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#0F172A]">Delete Document</DialogTitle>
            <DialogDescription className="text-sm text-[#64748B] mt-1">
              Are you sure you want to delete <span className="font-medium text-[#0F172A]">{deleteDoc?.original_filename}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setDeleteDoc(null)}
              data-testid="cancel-delete-doc-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
              onClick={confirmDeleteDoc}
              data-testid="confirm-delete-doc-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
